import { useState, useEffect, useCallback } from 'react';
import { detectTopic, TOPIC_STYLE } from '../topicUtils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PersonalRecord {
  topic: string;
  bestTime: number;
  query: string;
  date: string;
  solveCount: number;
}

export type RecordStore = Record<string, PersonalRecord>;
export const RECORDS_KEY = 'gm_records_v1';

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function loadRecords(): RecordStore {
  try {
    return JSON.parse(localStorage.getItem(RECORDS_KEY) ?? '{}');
  } catch {
    return {};
  }
}

export function saveRecord(query: string, timeSec: number): { isPersonalBest: boolean; topic: string; prevBest: number | null } {
  const topic = detectTopic(query);
  const store = loadRecords();
  const prev = store[topic];
  const isPersonalBest = !prev || timeSec < prev.bestTime;

  store[topic] = {
    topic,
    bestTime: isPersonalBest ? timeSec : prev.bestTime,
    query: isPersonalBest ? query : prev.query,
    date: isPersonalBest ? new Date().toISOString() : prev.date,
    solveCount: (prev?.solveCount ?? 0) + 1,
  };
  localStorage.setItem(RECORDS_KEY, JSON.stringify(store));
  return { isPersonalBest, topic, prevBest: prev?.bestTime ?? null };
}

// ─── Personal Best Banner ─────────────────────────────────────────────────────

interface BannerProps {
  info: { topic: string; timeSec: number; prevBest: number | null } | null;
  onDismiss: () => void;
}

export function PersonalBestBanner({ info, onDismiss }: BannerProps) {
  useEffect(() => {
    if (!info) return;
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [info, onDismiss]);

  if (!info) return null;
  const style = TOPIC_STYLE[info.topic] ?? TOPIC_STYLE.General;

  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[90] animate-bounce-once"
      style={{ animation: 'slideDownFade 0.4s ease forwards' }}
    >
      <style>{`
        @keyframes slideDownFade {
          from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
      <div className="flex items-center gap-3 bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-700 shadow-xl rounded-2xl px-5 py-3 max-w-sm">
        <div className="w-10 h-10 flex items-center justify-center bg-amber-100 dark:bg-amber-900/40 rounded-xl flex-shrink-0">
          <span className="text-xl">🏆</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-black text-sm text-amber-600 dark:text-amber-400">Personal Best!</div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${style.badge} ${style.badgeText}`}>{info.topic}</span>
            <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{info.timeSec.toFixed(2)}s</span>
            {info.prevBest && (
              <span className="text-[10px] text-slate-400 line-through">{info.prevBest.toFixed(2)}s</span>
            )}
          </div>
        </div>
        <button onClick={onDismiss} className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer flex-shrink-0">
          <i className="ri-close-line text-sm"></i>
        </button>
      </div>
    </div>
  );
}

// ─── Records Panel ────────────────────────────────────────────────────────────

interface PanelProps {
  show: boolean;
  onClose: () => void;
  onSolve: (query: string) => void;
}

type SortKey = 'time' | 'topic' | 'count' | 'date';

export default function PersonalRecordsPanel({ show, onClose, onSolve }: PanelProps) {
  const [records, setRecords] = useState<PersonalRecord[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('time');
  const [search, setSearch] = useState('');

  const reload = useCallback(() => {
    const store = loadRecords();
    setRecords(Object.values(store));
  }, []);

  useEffect(() => {
    if (show) reload();
  }, [show, reload]);

  const sorted = [...records]
    .filter(r => !search || r.topic.toLowerCase().includes(search.toLowerCase()) || r.query.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortKey === 'time') return a.bestTime - b.bestTime;
      if (sortKey === 'count') return b.solveCount - a.solveCount;
      if (sortKey === 'date') return new Date(b.date).getTime() - new Date(a.date).getTime();
      return a.topic.localeCompare(b.topic);
    });

  const bestOverall = records.length > 0 ? records.reduce((best, r) => r.bestTime < best.bestTime ? r : best) : null;

  return (
    <>
      {show && <div className="fixed inset-0 bg-black/20 backdrop-blur-[1px] z-40" onClick={onClose} />}
      <div
        className={`fixed top-0 right-0 h-full w-[380px] bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 z-50 flex flex-col transition-transform duration-300 ${show ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ boxShadow: '-8px 0 32px rgba(0,0,0,0.14)' }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
              <span className="text-lg">🏆</span>
            </div>
            <div>
              <h3 className="text-white font-bold text-sm">Personal Records</h3>
              <p className="text-amber-100 text-xs">{records.length} topic{records.length !== 1 ? 's' : ''} tracked</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 text-white cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Best overall spotlight */}
        {bestOverall && (
          <div className="mx-4 mt-4 mb-1 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-xl p-3 flex items-center gap-3 flex-shrink-0">
            <span className="text-2xl">⚡</span>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wide">All-Time Best</div>
              <div className="font-black text-amber-700 dark:text-amber-300 text-lg">{bestOverall.bestTime.toFixed(2)}s</div>
              <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate font-mono">{bestOverall.query}</div>
            </div>
            <div className={`px-2 py-1 rounded-lg text-[10px] font-bold ${(TOPIC_STYLE[bestOverall.topic] ?? TOPIC_STYLE.General).badge} ${(TOPIC_STYLE[bestOverall.topic] ?? TOPIC_STYLE.General).badgeText}`}>
              {bestOverall.topic}
            </div>
          </div>
        )}

        {/* Search + sort */}
        <div className="px-4 py-3 space-y-2 flex-shrink-0">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
            <input
              type="text"
              placeholder="Filter topics…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-sm dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          <div className="flex gap-1">
            {(['time', 'topic', 'count', 'date'] as SortKey[]).map(k => (
              <button
                key={k}
                onClick={() => setSortKey(k)}
                className={`flex-1 py-1 rounded-lg text-[10px] font-bold transition-colors cursor-pointer capitalize ${sortKey === k ? 'bg-amber-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'}`}
              >
                {k === 'time' ? '⏱ Time' : k === 'topic' ? 'A–Z' : k === 'count' ? '# Solves' : '📅 Recent'}
              </button>
            ))}
          </div>
        </div>

        {/* Records list */}
        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
          {sorted.length === 0 && (
            <div className="text-center py-16">
              <span className="text-4xl block mb-3">🏁</span>
              <p className="text-sm text-slate-400 dark:text-slate-500">No records yet.</p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Enable Timer and solve problems to track personal bests!</p>
            </div>
          )}
          {sorted.map((r, i) => {
            const style = TOPIC_STYLE[r.topic] ?? TOPIC_STYLE.General;
            const medal = i === 0 && sortKey === 'time' ? '🥇' : i === 1 && sortKey === 'time' ? '🥈' : i === 2 && sortKey === 'time' ? '🥉' : null;
            return (
              <div key={r.topic} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3 group hover:border-amber-300 dark:hover:border-amber-700 transition-all">
                <div className="flex items-center gap-2 mb-2">
                  {medal && <span className="text-base flex-shrink-0">{medal}</span>}
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${style.badge} ${style.badgeText}`}>{r.topic}</span>
                  <span className="ml-auto font-black text-lg" style={{ color: style.dot }}>{r.bestTime.toFixed(2)}s</span>
                </div>
                <code className="text-[10px] text-slate-500 dark:text-slate-400 font-mono line-clamp-1 block mb-2">{r.query}</code>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-400">{r.solveCount} solve{r.solveCount !== 1 ? 's' : ''} · {new Date(r.date).toLocaleDateString()}</span>
                  <button
                    onClick={() => { onSolve(r.query); onClose(); }}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40 text-amber-600 dark:text-amber-400 text-[10px] font-bold cursor-pointer transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-play-fill"></i> Retry
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
